import { refreshSystemRegistries, type MigratedProject } from './migrate';
import { InvalidProjectPathError, normalizeProjectPath } from './paths';
import { systemFilePaths } from './topology';

/**
 * A project as the editor holds it: its files in tree order — the order the runtime loads them in, and the registries
 * list systems in — where its topology is, what was generated, and its empty folders. Every function here is pure: it
 * returns a new tree, or throws `InvalidProjectPathError` naming the path, why, and the refusal's code. These are the
 * tree's mechanics and the rules it cannot run without; `applyProjectOperation` (./operations) is what the editor and
 * an agent call, adding the rules a person's changes follow.
 */
export type ProjectTree = MigratedProject;

/** The root files `gotti dev` and `gotti playground` read. */
const SCAFFOLD_ROOT_FILES = new Set(['package.json', 'tsconfig.json', 'gotti.config.ts', 'playground.config.ts', 'sandbox.playground.ts', 'index.html', 'vite.config.ts']);

/** A file the project cannot run without — its topology files and the root files the tools read: never deleted or renamed. */
export function isLoadBearing(tree: ProjectTree, path: string): boolean {
  const target = normalizeProjectPath(path);
  return SCAFFOLD_ROOT_FILES.has(target) || Object.values(tree.topology).includes(target);
}

/** The sandbox's protected root files (`src/<side>/{api,types,utils,constants}.ts`): never deleted or cut; a rename stays allowed. */
export function isDefaultRootFile(path: string): boolean {
  return /^src\/(client|server|shared)\/(api|types|utils|constants)\.ts$/.test(normalizeProjectPath(path));
}

/** The two registries: the site rewrites them, so the editor shows them read-only. */
export function isManaged(tree: ProjectTree, path: string): boolean {
  const target = normalizeProjectPath(path);
  return target === tree.topology.clientRegistry || target === tree.topology.serverRegistry;
}

/** Whether `path` is a system file by the runtime's rule (`systemFilePaths`): adding, removing or renaming one changes a registry. */
export function isSystemFile(path: string): boolean {
  const file = { [normalizeProjectPath(path)]: '' };
  return systemFilePaths(file, 'client').length > 0 || systemFilePaths(file, 'server').length > 0;
}

/** Every folder that holds `path`, outermost first: `src/a/b.ts` → `src`, `src/a`. */
const foldersAbove = (path: string): string[] => {
  const segments = path.split('/');
  return segments.slice(0, -1).map((_, i) => segments.slice(0, i + 1).join('/'));
};

/**
 * A file of the project's that `path` would be on a case-insensitive disk — `path` itself, or one whose name differs
 * only in letter case — other than `except`, or `undefined`. What a new name is refused against; a file is acted on by
 * its exact path, since a v1 project can hold two files whose names differ only in case.
 */
const clashOf = (tree: ProjectTree, path: string, except?: string): string | undefined => {
  const lower = path.toLowerCase();
  return Object.keys(tree.files).find((key) => key !== except && key.toLowerCase() === lower);
};

/** `path` when the project has that file, exactly; else refused. */
const existing = (tree: ProjectTree, raw: string): string => {
  const key = normalizeProjectPath(raw);
  if (!Object.hasOwn(tree.files, key)) throw new InvalidProjectPathError(raw, 'the project has no such file', 'missing');
  return key;
};

/** Every folder of the project — the folders its files sit in and its empty folders, with their parents — in tree order. */
export function folderPaths(tree: ProjectTree): string[] {
  const seen = new Set<string>();
  for (const path of Object.keys(tree.files)) for (const folder of foldersAbove(path)) seen.add(folder);
  for (const folder of tree.folders) for (const each of [...foldersAbove(folder), folder]) seen.add(each);
  return [...seen];
}

/** Refuses `path` for a new file or folder (or a file `except` is renamed to): a file already there (any case), or a file where a folder must be. */
const refuseTaken = (tree: ProjectTree, raw: string, path: string, except?: string): void => {
  const clash = clashOf(tree, path, except);
  if (clash !== undefined) throw new InvalidProjectPathError(raw, `the project already has ${clash}`, 'exists');
  const blocking = foldersAbove(path).map((folder) => clashOf(tree, folder)).find((key) => key !== undefined);
  if (blocking !== undefined) throw new InvalidProjectPathError(raw, `${blocking} is a file, not a folder`, 'exists');
};

/** `folders` without the ones `path` now sits in: they are no longer empty. */
const foldersAfterAdding = (folders: string[], path: string): string[] => folders.filter((folder) => !path.startsWith(`${folder}/`));

/** `folders` plus `path`'s folder when `files` no longer holds anything in it: a folder outlives its last file, as on a disk. */
const foldersAfterRemoving = (folders: string[], files: Record<string, string>, path: string): string[] => {
  const parent = foldersAbove(path).at(-1);
  if (parent === undefined || folders.includes(parent)) return folders;
  return Object.keys(files).some((key) => key.startsWith(`${parent}/`)) ? folders : [...folders, parent];
};

/** The registries rewritten from the files as they are now — in place, keeping their position — or `tree` itself when current. */
export function refreshRegistries(tree: ProjectTree): ProjectTree {
  const fresh = refreshSystemRegistries(tree.files, tree.topology);
  if (Object.entries(fresh).every(([path, code]) => tree.files[path] === code)) return tree;
  const files: Record<string, string> = {};
  for (const [path, code] of Object.entries(tree.files)) files[path] = Object.hasOwn(fresh, path) ? fresh[path] : code;
  for (const [path, code] of Object.entries(fresh)) if (!Object.hasOwn(files, path)) files[path] = code;
  return { ...tree, files };
}

/**
 * `tree` with a new file — at the end of the tree order, or at position `at` (undoing a delete puts a file back where it
 * was); the registries follow when it is a system file.
 */
export function addFile(tree: ProjectTree, path: string, code: string, at?: number): ProjectTree {
  const target = normalizeProjectPath(path);
  refuseTaken(tree, path, target);
  const entries = Object.entries(tree.files);
  entries.splice(at === undefined ? entries.length : Math.max(0, Math.min(at, entries.length)), 0, [target, code]);
  const next = { ...tree, files: Object.fromEntries(entries), folders: foldersAfterAdding(tree.folders, target) };
  return isSystemFile(target) ? refreshRegistries(next) : next;
}

/** `tree` with a file's code replaced in place. The registries are refreshed by `refreshRegistries`, which the editor calls when typing pauses. */
export function writeFile(tree: ProjectTree, path: string, code: string): ProjectTree {
  const key = existing(tree, path);
  if (tree.files[key] === code) return tree;
  return { ...tree, files: { ...tree.files, [key]: code } };
}

/** `tree` without a file; its folder stays, and the registries follow when it was a system file. */
export function deleteFile(tree: ProjectTree, path: string): ProjectTree {
  const key = existing(tree, path);
  if (isLoadBearing(tree, key)) throw new InvalidProjectPathError(path, 'the project cannot run without it', 'protected');
  const files = { ...tree.files };
  delete files[key];
  const next = { ...tree, files, folders: foldersAfterRemoving(tree.folders, files, key) };
  return isSystemFile(key) ? refreshRegistries(next) : next;
}

/** `tree` with a file at a new path in the same place in the tree order; the registries follow when either path is a system file. */
export function renameFile(tree: ProjectTree, from: string, to: string): ProjectTree {
  const key = existing(tree, from);
  if (isLoadBearing(tree, key)) throw new InvalidProjectPathError(from, 'the project cannot run without it', 'protected');
  const target = normalizeProjectPath(to);
  refuseTaken(tree, to, target, key);
  const files: Record<string, string> = {};
  for (const [path, code] of Object.entries(tree.files)) files[path === key ? target : path] = code;
  const next = { ...tree, files, folders: foldersAfterAdding(foldersAfterRemoving(tree.folders, files, key), target) };
  return isSystemFile(key) || isSystemFile(target) ? refreshRegistries(next) : next;
}

/** `tree` with an empty folder — a folder a person made before putting a file in it. */
export function addFolder(tree: ProjectTree, path: string): ProjectTree {
  const target = normalizeProjectPath(path);
  refuseTaken(tree, path, target);
  const existing = folderPaths(tree).find((folder) => folder.toLowerCase() === target.toLowerCase());
  if (existing !== undefined) throw new InvalidProjectPathError(path, `the project already has the folder ${existing}`, 'exists');
  return { ...tree, folders: [...tree.folders, target] };
}

/** `path` is `folder` or under it. */
const isWithin = (path: string, folder: string): boolean => path === folder || path.startsWith(`${folder}/`);

/** Every file under `folder`, at any depth, in tree order. */
export function filesUnder(tree: ProjectTree, folder: string): string[] {
  const target = normalizeProjectPath(folder);
  return Object.keys(tree.files).filter((path) => path.startsWith(`${target}/`));
}

/** `raw` in its one spelling when the project has that folder exactly — one its files sit in, or an empty one; else refused. */
const existingFolder = (tree: ProjectTree, raw: string): string => {
  const key = normalizeProjectPath(raw);
  if (!folderPaths(tree).includes(key)) throw new InvalidProjectPathError(raw, 'the project has no such folder', 'missing');
  return key;
};

/** Refuses a change to `folder` when the project cannot run without a file under it, naming the first such file. */
const refuseLoadBearingIn = (tree: ProjectTree, raw: string, folder: string): void => {
  const held = filesUnder(tree, folder).find((path) => isLoadBearing(tree, path));
  if (held !== undefined) throw new InvalidProjectPathError(raw, `it holds ${held}, which the project cannot run without`, 'protected');
};

/** `folders` plus the folder `gone` sat in when nothing is left in it — a folder outlives what it held, as on a disk. */
const keepFolderOf = (gone: string, files: Record<string, string>, folders: string[]): string[] => {
  const parent = foldersAbove(gone).at(-1);
  if (parent === undefined) return folders;
  const holdsSomething = Object.keys(files).some((path) => path.startsWith(`${parent}/`)) || folders.some((folder) => isWithin(folder, parent));
  return holdsSomething ? folders : [...folders, parent];
};

/**
 * `tree` with a folder at a new path: every file under it moved, each in its place in the tree order, and its empty
 * folders with it; the registries follow when a moved file is, or becomes, a system file. Refused when the project has
 * no such folder, when `to` is inside it, when the project cannot run without a file under it, and when `to` is taken —
 * by a file or another folder, in any letter case (a change of case alone is a rename).
 */
export function renameFolder(tree: ProjectTree, from: string, to: string): ProjectTree {
  const source = existingFolder(tree, from);
  const target = normalizeProjectPath(to);
  if (target.startsWith(`${source}/`)) throw new InvalidProjectPathError(to, 'a folder cannot move into itself', 'invalid_path');
  refuseLoadBearingIn(tree, from, source);
  if (target === source) return tree;
  refuseTaken(tree, to, target);
  const clash = folderPaths(tree).find((folder) => !isWithin(folder, source) && folder.toLowerCase() === target.toLowerCase());
  if (clash !== undefined) throw new InvalidProjectPathError(to, `the project already has the folder ${clash}`, 'exists');
  const moved = (path: string): string => (isWithin(path, source) ? `${target}${path.slice(source.length)}` : path);
  const files: Record<string, string> = {};
  for (const [path, code] of Object.entries(tree.files)) files[moved(path)] = code;
  // The folders `target` now sits in hold it, so none of them is empty; the folder `source` left may be.
  const folders = keepFolderOf(source, files, foldersAfterAdding(tree.folders.map(moved), target));
  const next = { ...tree, files, folders };
  return filesUnder(tree, source).some((path) => isSystemFile(path) || isSystemFile(moved(path))) ? refreshRegistries(next) : next;
}

/**
 * `tree` without a folder: every file and empty folder under it removed, the folder it sat in kept; the registries
 * follow when a removed file was a system file. Refused when the project has no such folder, and when it cannot run
 * without a file under it.
 */
export function deleteFolder(tree: ProjectTree, path: string): ProjectTree {
  const folder = existingFolder(tree, path);
  refuseLoadBearingIn(tree, path, folder);
  const files: Record<string, string> = {};
  for (const [file, code] of Object.entries(tree.files)) if (!isWithin(file, folder)) files[file] = code;
  const next = { ...tree, files, folders: keepFolderOf(folder, files, tree.folders.filter((each) => !isWithin(each, folder))) };
  return filesUnder(tree, folder).some(isSystemFile) ? refreshRegistries(next) : next;
}

/** The folders of the AI's v1 view: the three sides, and `__tests__` for its root tests (the tree's `src/__tests__`). */
const AI_ROOTS = /^(client|server|shared|__tests__)\/.+/;

/**
 * The tree path of a path in the AI's v1 view (`client/systems/X.ts`, `__tests__/X.spec.ts`, with or without a leading
 * `/`) — the same path under `src/` — or `null` for one outside those folders.
 */
export function treePathOfV1Path(v1Path: string): string | null {
  const path = normalizeProjectPath(v1Path);
  return AI_ROOTS.test(path) ? `src/${path}` : null;
}

/** The AI's v1 path of a tree path: `src/<side or __tests__>/…` without `src/`, or `null` for a file the AI does not see. */
export function v1PathOfTreePath(treePath: string): string | null {
  const path = normalizeProjectPath(treePath);
  return path.startsWith('src/') && AI_ROOTS.test(path.slice(4)) ? path.slice(4) : null;
}
